// components/contracts/ContractClientCard.js
// Card informazioni cliente con distinzione residenza vs cantiere

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { User, MapPin, Phone, Mail, HardHat, Home } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const ContractClientCard = ({ contract }) => {
  // I dati cliente sono salvati direttamente nel contratto, non come oggetto lead annidato
  const clientName = contract.client_name || '';
  const clientEmail = contract.client_email || '';
  const clientPhone = contract.client_phone || '';
  const clientFiscalCode = contract.client_fiscal_code || '';
  
  // Indirizzi: customer (residenza) e site (cantiere)
  const customerAddress = contract.customer_address || contract.property_address || '';
  const customerCity = contract.customer_city || contract.property_city || '';
  const customerCap = contract.customer_cap || contract.property_cap || '';
  const customerProvince = contract.customer_province || '';
  
  const siteSameAsCustomer = contract.site_same_as_customer_address !== false;
  const siteAddress = contract.site_address || '';
  const siteCity = contract.site_city || '';
  const siteCap = contract.site_cap || '';
  const siteProvince = contract.site_province || '';
  
  // Costruisci stringa indirizzo completo
  const formatFullAddress = (addr, city, cap, prov) => {
    let parts = [];
    if (addr) parts.push(addr);
    if (city || cap || prov) {
      let locParts = [];
      if (cap) locParts.push(cap);
      if (city) locParts.push(city);
      if (prov) locParts.push(`(${prov})`);
      parts.push(locParts.join(' '));
    }
    return parts.join(', ');
  };
  
  const customerFullAddress = formatFullAddress(customerAddress, customerCity, customerCap, customerProvince);
  const siteFullAddress = siteSameAsCustomer 
    ? customerFullAddress 
    : formatFullAddress(siteAddress, siteCity, siteCap, siteProvince);

  return (
    <Card className="card-abitalo">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="w-5 h-5 text-[#FF7819]" />
          Dati Cliente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-[#FF7819]" />
          </div>
          <div>
            <p className="font-medium text-[#3C3C3C]">
              {clientName}
            </p>
            {clientFiscalCode && (
              <p className="text-sm text-[#737373]">C.F. {clientFiscalCode}</p>
            )}
          </div>
        </div>

        {/* Indirizzo Residenza Cliente */}
        {customerFullAddress && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-[#737373] font-medium">Residenza</p>
              <p className="text-[#3C3C3C]">{customerFullAddress}</p>
            </div>
          </div>
        )}
        
        {/* Indirizzo Cantiere (se diverso) */}
        {!siteSameAsCustomer && siteFullAddress && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <HardHat className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-[#737373] font-medium">Cantiere</p>
              <p className="text-[#3C3C3C]">{siteFullAddress}</p>
            </div>
          </div>
        )}
        
        {/* Se coincidono, mostra indicazione */}
        {siteSameAsCustomer && customerFullAddress && (
          <div className="text-xs text-green-700 bg-green-50 p-2 rounded flex items-center gap-1">
            <HardHat className="w-3 h-3" />
            Il cantiere coincide con l'indirizzo di residenza
          </div>
        )}

        {clientPhone && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <a href={`tel:${clientPhone}`} className="text-[#3C3C3C] hover:underline">
              {clientPhone}
            </a>
          </div>
        )}

        {clientEmail && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <a href={`mailto:${clientEmail}`} className="text-[#3C3C3C] hover:underline truncate">
              {clientEmail}
            </a>
          </div>
        )}

        {/* Importo contratto */}
        <div className="pt-3 mt-3 border-t">
          <div className="flex justify-between items-center">
            <span className="text-[#737373]">Importo Contratto</span>
            <span className="text-xl font-bold text-[#FF7819]">
              {formatCurrency(contract.contract_amount || 0)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContractClientCard;
